import React, { useEffect, useState } from 'react';
import { Row, Column } from 'react-foundation';
import { NewFollowsListPopup } from '../shared/NewFollowsListPopup';
import * as ArticlesAPI from '../../../api/ArticlesAPI';
import * as FollowsListAPI from '../../../api/FollowsListAPI';
import { StatusPopup } from '../shared/StatusPopup';
import WhiteLoadingSpinner from '../../../../assets/images/loading-spinner-white.svg';
import { getArticleIdFromURL } from '../../utils';
import { SafeImagePreview } from '../shared/SafeImagePreview';

const ShowArticle = ({ currentUser, currentUserCanEdit }) => {
	const [showAddFollowPopup, setShowAddFollowPopup] = useState(false);
	const [showStatusObject, setShowStatusObject] = useState([]);
	const [followsLists, setFollowsLists] = useState([]);
	const [show, setShow] = useState(false);
	const [isApproveLoading, setIsApproveLoading] = useState(false);

	const [article, setArticle] = useState(undefined);

	useEffect(() => {
		const id = getArticleIdFromURL(window.location.href);
		fetchArticle(id);
		fetchFollowsList();
	}, []);

	const fetchArticle = async (id) => {
		const { data } = await ArticlesAPI.getArticleByID(id);
		setArticle(data);
	};

	const fetchFollowsList = async () => {
		try {
			let response = await FollowsListAPI.fetchAllLists();

			let tempFollowsList = [];
			for (const list of response.data) {
				let tempList = {
					name: list.name,
					id: list.id,
					organization_ids: [],
					article_ids: [],
				};
				for (const org of list?.organizations) {
					tempList.organization_ids.push(org?.organization?.id);
				}
				for (const article of list?.articles) {
					tempList.article_ids.push(article?.article?.id);
				}
				tempFollowsList.push(tempList);
			}
			response.data = tempFollowsList;
			setFollowsLists(response.data);
		} catch (e) {
			console.error(e);
		}
	};

	const redirectToEditArticle = () => {
		location.assign(`/articles/${article.id}/edit`);
	};

	/**
	 * Handles clicking of the follow/unfollow button and bringing up the follows list popup
	 */
	const handleFollowOrg = () => {
		if (currentUser) {
			setShowAddFollowPopup(!showAddFollowPopup);
		} else {
			location.assign('/users/sign_up');
		}
	};

	/**
	 * Convert date strings from YYYY/MM/DD to 'MMM DD, YYYY'
	 * @param timeString the time string that will be altered
	 * @return {{String}} returns the formatted date string
	 */
	const convertDateToLongDate = (timeString) => {
		let date = new Date(timeString);
		let month = date.toLocaleString('default', { month: 'long' });
		return month + ' ' + date.getDate() + ', ' + date.getFullYear();
	};

	/**
	 * Redirect the user to the organizations directory with the current interest area selected as a filter
	 * @param interestAreaID the id of the interest area clicked
	 */
	const redirectToOrganizationsDirectoryWithInterestArea = (
		interestAreaID
	) => {
		window.open('/organizations?interest_area=' + interestAreaID);
	};

	/**
	 * Checks whether the article is currently followed
	 * @return {boolean} true if followed, false if not
	 */
	const isArticleFollowed = () => {
		for (const list of followsLists) {
			if (list.article_ids?.includes(article.id)) {
				return true;
			}
		}
		return false;
	};

	/**
	 * Calls api to approve article
	 */
	const approveArticle = () => {
		setIsApproveLoading(true);
		ArticlesAPI.approveArticle(article.id).then((response) => {
			let tempShowStatusObject;
			if (response.status === 200) {
				article.approved = true;
				setArticle(article);

				tempShowStatusObject = {
					type: 'success',
					text: 'Successfully approved article.',
				};
			} else {
				tempShowStatusObject = {
					type: 'failure',
					text: 'Failed to approve article. Please try again.',
				};
			}

			setIsApproveLoading(false);
			setShowStatusObject(tempShowStatusObject);
			setShow(true);
		});
	};

	/**
	 * Calls api to unapprove article
	 */
	// yucky. Let's DRY this up when we get a chance
	const unapproveArticle = () => {
		setIsApproveLoading(true);
		ArticlesAPI.unapproveArticle(article.id).then((response) => {
			let tempShowStatusObject;
			if (response.status === 200) {
				article.approved = false;
				setArticle(article);

				tempShowStatusObject = {
					type: 'success',
					text: 'Successfully un-approved article.',
				};
			} else {
				tempShowStatusObject = {
					type: 'failure',
					text: 'Failed to approve article. Please try again.',
				};
			}

			setIsApproveLoading(false);
			setShowStatusObject(tempShowStatusObject);
			setShow(true);
		});
	};

	if (!article) {
		return <p>Loading...</p>; // TODO: loading screen
	}

	return (
		<div className="show-article-page">
			<Row>
				<Column small={16} medium={8}>
					<div className="top-button-container">
						{currentUserCanEdit ? (
							<div
								className="edit-button"
								onClick={() => redirectToEditArticle()}
							>
								<p>
									Edit Article
									<i className="fas fa-edit cursor-pointer" />
								</p>
							</div>
						) : (
							<>&nbsp;</>
						)}

						{isArticleFollowed() ? (
							<div
								className="primary-button follow-button float-right"
								onClick={handleFollowOrg}
							>
								<p>Following</p>
							</div>
						) : (
							<div
								className="secondary-button follow-button float-right"
								onClick={handleFollowOrg}
							>
								<p>Follow</p>
							</div>
						)}
					</div>

					<h3 className="title-font">{article?.title}</h3>
					<p className="body-font">
						{article?.author +
							' Posted on ' +
							(article?.posted_on
								? convertDateToLongDate(article?.posted_on)
								: '')}
					</p>
				</Column>

				{show ? (
					<StatusPopup
						statusPopupObject={showStatusObject}
						setShowStatusPopup={setShow}
					/>
				) : (
					''
				)}

				{showAddFollowPopup ? (
					<NewFollowsListPopup
						followsListsParam={followsLists}
						setShow={setShowAddFollowPopup}
						articleID={article.id}
						current_user={currentUser}
						setShowStatusPopup={setShowStatusObject}
						setShowStatus={setShow}
					/>
				) : (
					''
				)}
			</Row>
			<Row>
				<Column small={16} medium={8}>
					<SafeImagePreview
						article={article}
						className="main-image"
					/>

					{/* {article?.img_url ? (
						<img
							src={article?.img_url}
							alt="Main image"
							className="main-image"
						/>
					) : (
						''
					)} */}

					<p
						className="body-font"
						dangerouslySetInnerHTML={{ __html: article?.body }}
					/>
					{currentUser?.admin ? (
						<Row className="thinner-horizontal-margins">
							<Column small={12}>
								{article?.approved ? (
									<div className="approve-banner-inner">
										<p className="header-font">
											This article is currently approved.
											Would you like to unapprove it?
										</p>
										<div
											className="primary-button"
											onClick={() => unapproveArticle()}
										>
											{isApproveLoading ? (
												<img
													id="file-upload-loading-spinner"
													src={WhiteLoadingSpinner}
													alt="Loading spinner"
												/>
											) : (
												<p>Unapprove</p>
											)}
										</div>
									</div>
								) : (
									<div className="approve-banner-inner">
										<p className="header-font">
											This article is currently
											unapproved. Would you like to
											approve it?
										</p>
										<div
											className="primary-button"
											onClick={() => approveArticle()}
										>
											{isApproveLoading ? (
												<img
													id="file-upload-loading-spinner"
													src={WhiteLoadingSpinner}
													alt="Loading spinner"
												/>
											) : (
												<p>Approve</p>
											)}
										</div>
									</div>
								)}
							</Column>
						</Row>
					) : (
						''
					)}
				</Column>
				<Column
					small={8}
					medium={4}
					className="full-width-mobile-column"
				>
					<h3 className="header-font right-side-headers">
						Connected to this article
					</h3>
					<div className="orgs-container">
						{article?.organizations?.map((organization, index) => {
							return (
								<a
									href={`/organizations/${organization.slug}`}
									target="_blank"
									className="body-font link-font"
									key={index}
								>
									{organization.legal_name}
								</a>
							);
						})}
					</div>
					<h3 className="header-font right-side-headers">
						Interest areas in this article
					</h3>
					<div id="interest-tag-area" className="row">
						<div className="columns small-12 button-group">
							{article?.interest_areas?.map(
								(interestArea, index) => {
									return (
										<div
											className="tag-button"
											id={interestArea.id}
											key={interestArea.name}
											onClick={() =>
												redirectToOrganizationsDirectoryWithInterestArea(
													interestArea.id
												)
											}
										>
											<label>
												<div
													id={
														'sponsorable-opportunity-' +
														interestArea.id
													}
												>
													<p className="body-font">
														{interestArea.name}
													</p>
												</div>
											</label>
										</div>
									);
								}
							)}
						</div>
					</div>
				</Column>
			</Row>
		</div>
	);
};

export default ShowArticle;
